import { useState, useEffect, useCallback } from 'react'
import { notificationService } from '../services/notifications'
import { useAuth } from './useAuth'

export const useNotifications = () => {
  const { user } = useAuth()
  const [notifications, setNotifications] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [unreadCount, setUnreadCount] = useState(0)

  const loadNotifications = useCallback(async () => {
    if (!user?.merchantId) {
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      setError(null)
      const data = await notificationService.getMerchantNotifications(user.merchantId)
      setNotifications(data)
      setUnreadCount(data.filter((n: any) => !n.isRead).length)
    } catch (err: any) {
      setError(err.message || 'Failed to load notifications')
    } finally {
      setLoading(false)
    }
  }, [user?.merchantId])

  useEffect(() => {
    loadNotifications()
  }, [loadNotifications])

  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await notificationService.markAsRead(notificationId)
      setNotifications(prev =>
        prev.map(n => n.id === notificationId ? { ...n, isRead: true } : n)
      )
      setUnreadCount(prev => Math.max(0, prev - 1))
    } catch (err: any) {
      setError(err.message || 'Failed to mark as read')
    }
  }, [])

  const markAllAsRead = useCallback(async () => {
    try {
      await Promise.all(
        notifications.filter(n => !n.isRead).map(n => notificationService.markAsRead(n.id))
      )
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })))
      setUnreadCount(0)
    } catch (err: any) {
      setError(err.message || 'Failed to mark all as read')
    }
  }, [notifications])

  return {
    notifications,
    loading,
    error,
    unreadCount,
    markAsRead,
    markAllAsRead,
    refresh: loadNotifications
  }
}